import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../Config/Api";
import { ActionType } from "../../types/actionType";
import { AppType } from "../../types/appType";
import { ErrorType } from "../../types/errorType";
import { RootState } from "../store";
import { ExtractErrorInfo } from "../../components/shared/ExtractErrorInfo";
import { API_ENDPOINTS } from "Config/apiConfig";
import { ActionTargets, ActionTargetsEnum } from "Config/Enum/actionTargetsEnum";
import { mapIdToValue } from "utils/mapIdToValue";

export interface AppTypeWithActionType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  actionType: ActionType;
}

export const initialState: AppTypeWithActionType = {
  loading: false,
  error: null,
  actionType: {
    id: "",
    organisationId: "1", //"UI1-123",
    name: "",
    target: ActionTargets.REPAIRER.id,
    delayBeforeFirstActionMinutes: 0,
    slaDeadlineMinutes: 0,
    eventText: "",
    active: true,
    actionTargets: [ActionTargets.REPAIRER, ActionTargets.OPERATOR],
    audit: {
      createdByUserId: "",
      createdAtUTC: null,
      lastUpdatedByUserId: "",
      lastUpdatedAtUTC: null,
    },
  },
};

// Thunk to fetch action by ID (for edit mode)
export const fetchActionById = createAsyncThunk<ActionType, string>(
  "action/fetchActionById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ActionType>(`${API_ENDPOINTS.actions}/${id}`);
      return data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

interface SaveActionTypeParams {
  formData: ActionType;
  actionId?: string;
}

// Thunk to handle both creating and editing action types
export const saveActionType = createAsyncThunk<ActionType, SaveActionTypeParams>(
  "action/saveActionType",
  async ({ formData, actionId }, { dispatch, rejectWithValue }) => {
    try {
      // Ensure target is always set for API payload without mutating formData used by the form
      const targetName = mapIdToValue(formData.target, initialState.actionType.actionTargets);
      const payload: ActionType = {
        ...formData,
        // Map the selected target id to the API's expected value/name
        target: targetName || ActionTargets.REPAIRER.id,
      };

      let response;
      if (actionId) {
        // If actionId exists, update (PATCH request)
        response = await api.patch<ActionType>(`${API_ENDPOINTS.actions}/${actionId}`, payload);
      } else {
        // If actionId does not exist, create new (POST request)
        response = await api.post<ActionType>(API_ENDPOINTS.actions, payload);
      }

      return response.data;
    } catch (error: any) {
      // Save the form in the state, ensuring target is valid
      const safeForm: ActionType = {
        ...formData,
        // Keep target as an id in Redux/form state so radios continue to match
        target: formData.target || ActionTargets.REPAIRER.id,
      };
      dispatch(setForm(safeForm));
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

const actionSlice = createSlice({
  name: "action",
  initialState,
  reducers: {
    setForm: (state, action: PayloadAction<ActionType>) => {
      state.actionType = action.payload;
    },
    clearActionError: (state) => {
      return {
        ...state,
        error: null,
      };
    },
    resetActionForm: (state) => {
      return {
        ...state,
        actionType: initialState.actionType,
      };
    },
  },
  extraReducers: (builder) => {
    // Handle fetchActionById cases
    builder.addCase(fetchActionById.fulfilled, (state, action) => {
      const targetEnum = ActionTargetsEnum[action.payload.target as keyof typeof ActionTargetsEnum];
      const targetId = ActionTargets[targetEnum]?.id; // Map the target to its corresponding id

      return {
        ...state,
        actionType: {
          ...state.actionType,
          id: action.payload.id,
          name: action.payload.name,
          target: targetId || action.payload.target, // Use target id, fallback to original value if not found
          delayBeforeFirstActionMinutes: action.payload.delayBeforeFirstActionMinutes,
          slaDeadlineMinutes: action.payload.slaDeadlineMinutes,
          organisationId: action.payload.organisationId,
          eventText: action.payload.eventText,
          active: action.payload.active,
          actionTargets: state.actionType.actionTargets,
        },
        loading: false,
        error: null,
      };
    });
    builder.addCase(fetchActionById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchActionById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });

    // Handle saveActionType cases
    builder.addCase(saveActionType.fulfilled, (state) => {
      return {
        ...state,
        error: null,
        loading: false,
        actionType: initialState.actionType, // Reset the form after a successful response
      };
    });
    builder.addCase(saveActionType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(saveActionType.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
  },
});

export const { setForm, clearActionError, resetActionForm } = actionSlice.actions;

export const actionRootState = (state: RootState) => state;
export default actionSlice.reducer;
